export type DimensionKey = 'autoperception' | 'evidence' | 'impact' | 'community' | 'maturity';
export type DevelopmentLevel = 'Interesse inicial' | 'Potencial' | 'Em desenvolvimento' | 'Confirmado' | 'Multiplicador';
export type ConfidenceLevel = 'Baixo' | 'Moderado' | 'Alto';

export const DIMENSIONS: Record<DimensionKey, { label: string; weight: number }> = {
  autoperception: { label: 'Autopercepção', weight: .15 },
  evidence: { label: 'Evidência comportamental', weight: .30 },
  impact: { label: 'Frutos e impacto', weight: .25 },
  community: { label: 'Confirmação comunitária', weight: .20 },
  maturity: { label: 'Prontidão e maturidade', weight: .10 },
};

export type DimensionScores = Partial<Record<DimensionKey, number>>;
export type ConfidenceInput = { answered: number; total: number; durationSeconds?: number; contradictions?: number; uniformAnswers?: boolean; practicalExamples?: number; communityReviews?: number; convergence?: number; opportunitiesSkipped?: number };

const clamp = (value: number) => Math.max(0, Math.min(100, Math.round(value)));

/** Integrates only dimensions with evidence; lack of opportunity is not scored as lack of gift. */
export function integratedScore(scores: DimensionScores): number {
  const present = (Object.keys(DIMENSIONS) as DimensionKey[]).filter(key => typeof scores[key] === 'number');
  if (!present.length) return 0;
  const denominator = present.reduce((sum, key) => sum + DIMENSIONS[key].weight, 0);
  return clamp(present.reduce((sum, key) => sum + (scores[key] || 0) * DIMENSIONS[key].weight, 0) / denominator);
}

export function developmentLevel(scores: DimensionScores, integrated: number, multiplierEvidence = false): DevelopmentLevel {
  const evidence = scores.evidence || 0, impact = scores.impact || 0, community = scores.community || 0;
  if (multiplierEvidence && evidence >= 70 && impact >= 70 && community >= 65) return 'Multiplicador';
  if (integrated >= 70 && evidence >= 60 && impact >= 55 && community >= 55) return 'Confirmado';
  if (integrated >= 50 && evidence >= 35 && impact >= 35) return 'Em desenvolvimento';
  if (integrated >= 30 || (scores.autoperception || 0) >= 55) return 'Potencial';
  return 'Interesse inicial';
}

export function confidence(input: ConfidenceInput): { score: number; level: ConfidenceLevel; reasons: string[] } {
  const completion = input.total ? input.answered / input.total : 0;
  let score = completion * 35;
  const reasons: string[] = [];
  if (completion >= .95) { score += 15; reasons.push('inventário respondido quase integralmente'); } else reasons.push('há respostas pendentes');
  if ((input.contradictions || 0) === 0) { score += 12; reasons.push('respostas coerentes entre itens relacionados'); } else { score -= Math.min(18, (input.contradictions || 0) * 6); reasons.push('foram encontradas respostas potencialmente contraditórias'); }
  if (input.uniformAnswers) { score -= 12; reasons.push('muitas respostas iguais reduzem a precisão'); }
  if ((input.durationSeconds || 0) >= Math.max(120, input.total * 3)) { score += 8; reasons.push('tempo de resposta compatível com leitura atenta'); } else if (input.durationSeconds !== undefined) reasons.push('conclusão muito rápida reduz a confiança');
  if ((input.practicalExamples || 0) > 0) { score += Math.min(12, (input.practicalExamples || 0) * 3); reasons.push('há evidências práticas registradas'); }
  if ((input.communityReviews || 0) > 0) { score += Math.min(12, (input.communityReviews || 0) * 4); reasons.push('há confirmação comunitária disponível'); }
  if ((input.convergence || 0) >= 60) { score += 6; reasons.push('autopercepção e avaliação externa convergem'); }
  const final = clamp(score);
  return { score: final, level: final >= 75 ? 'Alto' : final >= 45 ? 'Moderado' : 'Baixo', reasons };
}

export const frequencyScale = [
  { value: 0, label: 'Nunca ocorreu' }, { value: 1, label: 'Ocorreu uma vez' }, { value: 2, label: 'Ocorreu algumas vezes' },
  { value: 3, label: 'Ocorre mensalmente' }, { value: 4, label: 'Ocorre semanalmente' }, { value: 5, label: 'Ocorre várias vezes por semana' },
  { value: -1, label: 'Nunca tive oportunidade de experimentar' },
];
